// @ts-check

/**
 * Ring size converter — see blocks/ring-size-converter.liquid.
 *
 * Reads the conversion table embedded in the block as JSON (rendered from
 * snippets/ring-size-data.liquid, the same source as the ring size chart)
 * and converts either a measurement or a known size in any standard into
 * every equivalent. Pure client-side lookup — no network requests.
 */

import { Component } from '@theme/component';

import { decodeEntitiesDeep } from '@theme/utilities';
/** Column indexes in each data row (matches snippets/ring-size-data.liquid). */
const COL = {
  diameterMm: 0,
  diameterIn: 1,
  circumferenceMm: 2,
  circumferenceIn: 3,
  uk: 4,
  us: 5,
  fr: 6,
  de: 7,
  jp: 8,
  ch: 9,
};

/** Standards offered in the "I know a ring size" dropdown. */
const STANDARDS = {
  uk: { column: COL.uk, label: 'UK & Ireland' },
  us: { column: COL.us, label: 'USA & Canada' },
  fr: { column: COL.fr, label: 'French' },
  de: { column: COL.de, label: 'German' },
  jp: { column: COL.jp, label: 'Japanese' },
  ch: { column: COL.ch, label: 'Swiss' },
};

const MM_PER_UNIT = { mm: 1, cm: 10, in: 25.4 };

/**
 * The largest gap between adjacent circumference values is ~2.6mm (baby
 * sizes); within the letter range it is ~0.7mm. A match further than
 * HALF_STEP_MM from a row is flagged as "between sizes".
 */
const HALF_STEP_MM = 0.36;

/** Beyond the table ends by more than this → out of range, no result. */
const RANGE_TOLERANCE_MM = 1.5;

/**
 * @typedef {object} RingSizeConverterRefs
 * @property {HTMLInputElement} valueInput
 * @property {HTMLSelectElement} unitSelect
 * @property {HTMLSelectElement} dimensionSelect
 * @property {HTMLSelectElement} standardSelect
 * @property {HTMLSelectElement} sizeSelect
 * @property {HTMLElement} measurePanel
 * @property {HTMLElement} sizePanel
 * @property {HTMLElement} result
 * @property {HTMLScriptElement} data
 */

/**
 * @extends {Component<RingSizeConverterRefs>}
 */
class RingSizeConverter extends Component {
  requiredRefs = [
    'valueInput',
    'unitSelect',
    'dimensionSelect',
    'standardSelect',
    'sizeSelect',
    'measurePanel',
    'sizePanel',
    'result',
    'data',
  ];

  /** @type {string[][]} */
  #rows = [];

  /**
   * Locale strings injected by snippets/ring-size-converter-form.liquid
   * (ref="i18n"). Every lookup falls back to the built-in English string,
   * so a missing/old snippet can never break the converter.
   * @type {Record<string, any>}
   */
  #i18n = {};

  /** @type {'measure' | 'size'} */
  #mode = 'measure';

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#rows = JSON.parse(this.refs.data.textContent ?? '[]');
    } catch {
      this.#rows = [];
    }

    try {
      // `t` escapes these; they are written with textContent. See @theme/utilities.
      this.#i18n = decodeEntitiesDeep(JSON.parse(this.refs.i18n?.textContent ?? 'null') ?? {});
    } catch {
      this.#i18n = {};
    }

    this.#populateSizeOptions();
  }

  /**
   * Called by the theme's Component base class after a Section Rendering
   * morph touches this subtree (e.g. the product page size guide modal
   * after a variant change). The morphed HTML only contains the
   * server-rendered placeholder option, so rebuild the size dropdown.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#populateSizeOptions();
  }

  /**
   * Switches between "I have a measurement" and "I know a ring size".
   *
   * @param {Event} event - Change event from a mode radio (`on:change="/setMode"`).
   */
  setMode(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#mode = input.value === 'size' ? 'size' : 'measure';

    this.refs.measurePanel.hidden = this.#mode !== 'measure';
    this.refs.sizePanel.hidden = this.#mode !== 'size';

    this.convert();
  }

  /**
   * Rebuilds the size dropdown for the newly selected standard
   * (`on:change="/changeStandard"`).
   */
  changeStandard() {
    this.#populateSizeOptions();
    this.convert();
  }

  /**
   * Recalculates and renders the result (`on:input` / `on:change="/convert"`).
   */
  convert() {
    this.#mode === 'measure' ? this.#convertFromMeasurement() : this.#convertFromKnownSize();
  }

  #populateSizeOptions() {
    const { standardSelect, sizeSelect } = this.refs;
    const standard = STANDARDS[/** @type {keyof typeof STANDARDS} */ (standardSelect.value)] ?? STANDARDS.uk;

    const previous = sizeSelect.value;
    sizeSelect.options.length = 1;

    for (const row of this.#rows) {
      const value = row[standard.column];
      if (value === '-') continue;
      sizeSelect.add(new Option(value, value));
    }

    // Keep the selection when it survives the standard change (e.g. editor reloads).
    if (previous && [...sizeSelect.options].some((option) => option.value === previous)) {
      sizeSelect.value = previous;
    }
  }

  #convertFromMeasurement() {
    const { valueInput, unitSelect, dimensionSelect } = this.refs;
    // Accept a decimal comma (52,5) as well as a decimal point (52.5).
    const raw = parseFloat(valueInput.value.replace(',', '.'));

    if (!Number.isFinite(raw) || raw <= 0) {
      this.#clearResult();
      return;
    }

    const mm = raw * (MM_PER_UNIT[/** @type {keyof typeof MM_PER_UNIT} */ (unitSelect.value)] ?? 1);
    const column = dimensionSelect.value === 'diameter' ? COL.diameterMm : COL.circumferenceMm;

    // Only rows with a UK size are meaningful results for our customers.
    const candidates = this.#rows.filter((row) => row[COL.uk] !== '-');
    if (candidates.length === 0) return;

    const first = candidates[0];
    const last = candidates[candidates.length - 1];
    const tolerance = column === COL.diameterMm ? RANGE_TOLERANCE_MM / Math.PI : RANGE_TOLERANCE_MM;

    if (mm < parseFloat(first[column]) - tolerance || mm > parseFloat(last[column]) + tolerance) {
      this.#renderMessage(
        (this.#i18n.outOfRange ??
          'That measurement is outside the standard UK range ({min} to {max}). Double-check whether it is the circumference or the diameter, and that the unit is right.')
          .replace('{min}', first[COL.uk])
          .replace('{max}', last[COL.uk])
      );
      return;
    }

    let best = candidates[0];
    let bestDiff = Infinity;

    for (const row of candidates) {
      const diff = Math.abs(parseFloat(row[column]) - mm);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = row;
      }
    }

    const halfStep = column === COL.diameterMm ? HALF_STEP_MM / Math.PI : HALF_STEP_MM;
    const note =
      bestDiff > halfStep
        ? (this.#i18n.betweenSizes ??
            'Your measurement falls between sizes — if in doubt, choose the larger size.')
        : '';

    this.#renderResult(best, note);
  }

  #convertFromKnownSize() {
    const { standardSelect, sizeSelect } = this.refs;

    if (!sizeSelect.value) {
      this.#clearResult();
      return;
    }

    const standard = STANDARDS[/** @type {keyof typeof STANDARDS} */ (standardSelect.value)] ?? STANDARDS.uk;
    const row = this.#rows.find((candidate) => candidate[standard.column] === sizeSelect.value);

    row ? this.#renderResult(row, '') : this.#clearResult();
  }

  /**
   * @param {string[]} row - The matched data row.
   * @param {string} note - Optional guidance shown under the headline size.
   */
  #renderResult(row, note) {
    const uk = row[COL.uk];
    const labels = this.#i18n.labels ?? {};
    const headline =
      uk === '-'
        ? (this.#i18n.noUk ?? 'No UK equivalent for this size')
        : (this.#i18n.ukSize ?? 'UK size {size}').replace('{size}', uk);

    const equivalents = [
      [labels.us ?? 'USA & Canada', row[COL.us]],
      [labels.fr ?? 'French', row[COL.fr]],
      [labels.de ?? 'German', row[COL.de]],
      [labels.jp ?? 'Japanese', row[COL.jp]],
      [labels.ch ?? 'Swiss', row[COL.ch]],
      [labels.diameter ?? 'Inside diameter', `${row[COL.diameterMm]} mm`],
      [labels.circumference ?? 'Inside circumference', `${row[COL.circumferenceMm]} mm`],
    ]
      .filter(([, value]) => value !== '-')
      .map(
        ([label, value]) =>
          `<div><dt>${label}</dt><dd>${value}</dd></div>`
      )
      .join('');

    this.refs.result.innerHTML = `
      <p class="ring-size-converter__result-size">${headline}</p>
      ${note ? `<p class="ring-size-converter__result-note">${note}</p>` : ''}
      <dl class="ring-size-converter__result-grid">${equivalents}</dl>
      ${this.#renderShopLinks(uk)}
    `;
    this.refs.result.hidden = false;
  }

  /**
   * "Shop rings in size X" buttons for the matched UK size, linking to the
   * configured collection filtered by the Size option. Letter sizes only
   * (variants are filtered by letter): a half/quarter size offers both
   * neighbouring letters, Z+ sizes offer Z. Only letters listed in
   * data-shop-sizes (i.e. actually stocked) are offered.
   *
   * @param {string} uk - The matched UK size, e.g. "M", "M 1/2", "Z+2".
   * @returns {string} HTML for the shop buttons, or '' when not applicable.
   */
  #renderShopLinks(uk) {
    const { shopUrl, shopParam, shopSizes } = this.refs.result.dataset;

    if (!shopUrl || !shopParam || !shopSizes || uk === '-') return '';

    const letterMatch = uk.match(/^([A-Z])/);
    if (!letterMatch) return '';

    const letter = letterMatch[1];

    /** @type {string[]} */
    let letters;
    if (uk === letter) {
      letters = [letter];
    } else if (uk.startsWith('Z')) {
      letters = ['Z'];
    } else {
      // Half/quarter size: offer the letters either side.
      letters = [letter, String.fromCharCode(letter.charCodeAt(0) + 1)];
    }

    const stocked = new Set(shopSizes.split(',').map((size) => size.trim().toUpperCase()));
    const buttons = letters
      .filter((size) => stocked.has(size))
      .map(
        (size) =>
          `<a class="button button-secondary ring-size-converter__shop-link" href="${shopUrl}?${shopParam}=${encodeURIComponent(size)}">${(this.#i18n.shopLink ?? 'Shop rings in size {size}').replace('{size}', size)}</a>`
      )
      .join('');

    return buttons ? `<div class="ring-size-converter__shop">${buttons}</div>` : '';
  }

  /**
   * @param {string} message
   */
  #renderMessage(message) {
    this.refs.result.innerHTML = `<p class="ring-size-converter__result-note">${message}</p>`;
    this.refs.result.hidden = false;
  }

  #clearResult() {
    this.refs.result.hidden = true;
    this.refs.result.innerHTML = '';
  }
}

if (!customElements.get('ring-size-converter-component')) {
  customElements.define('ring-size-converter-component', RingSizeConverter);
}
